import Phaser from "phaser";
import { TILE, worldToScreen, type ScreenPos } from "../map/Iso";
import { PROP_TEXTURE_KEY } from "../map/MapBuilder";

const ELEV_PX = 6;
const MAX_GROWTH = 3;
const GROW_DURATION_MS = 600;

export class InteractableTree {
  readonly worldX: number;
  readonly worldY: number;
  readonly image: Phaser.GameObjects.Image;
  private growthLevel = 0;
  private growing = false;
  private fruits: Phaser.GameObjects.Arc[] = [];

  constructor(scene: Phaser.Scene, origin: ScreenPos, tx: number, ty: number, elevation: number) {
    this.worldX = tx;
    this.worldY = ty;

    const elevPx = elevation * ELEV_PX;
    const { sx, sy } = worldToScreen(tx, ty);
    this.image = scene.add.image(
      origin.sx + sx + TILE.halfW,
      origin.sy + sy + TILE.height - 4 - elevPx,
      PROP_TEXTURE_KEY,
    );
    this.image.setOrigin(0.5, 1);
    this.image.setDepth((tx + ty) * 1000 + 1);
  }

  get isMaxGrowth(): boolean {
    return this.growthLevel >= MAX_GROWTH;
  }

  get isGrowing(): boolean {
    return this.growing;
  }

  /** Increases the tree size with a tween. Returns false if already at max. */
  grow(scene: Phaser.Scene): boolean {
    if (this.growing || this.growthLevel >= MAX_GROWTH) return false;
    this.growing = true;
    this.growthLevel++;
    const targetScale = 1 + this.growthLevel * 0.25;
    scene.tweens.add({
      targets: this.image,
      scaleX: targetScale,
      scaleY: targetScale,
      duration: GROW_DURATION_MS,
      ease: "Back.easeOut",
      onComplete: () => {
        this.growing = false;
      },
    });
    return true;
  }

  /** Add fruit circles around the tree foliage. Call once at max growth. */
  addFruit(scene: Phaser.Scene): void {
    if (this.fruits.length > 0) return;

    const depth = (this.worldX + this.worldY) * 1000 + 2;
    const cx = this.image.x;
    const cy = this.image.y;
    // Foliage center is roughly at the top of the tree image
    const scale = 1 + MAX_GROWTH * 0.25; // 1.75
    const foliageY = cy - 22 * scale;
    const foliageR = 12 * scale;

    const fruitColors = [0xe84040, 0xf0a020, 0xe84040, 0xf06020, 0xe84040];
    const positions = [
      { angle: -60, rFrac: 0.7 },
      { angle: -10, rFrac: 0.85 },
      { angle: 40, rFrac: 0.65 },
      { angle: 130, rFrac: 0.75 },
      { angle: 200, rFrac: 0.6 },
    ];

    for (let i = 0; i < positions.length; i++) {
      const p = positions[i]!;
      const rad = (p.angle * Math.PI) / 180;
      const fx = cx + Math.cos(rad) * foliageR * p.rFrac;
      const fy = foliageY + Math.sin(rad) * foliageR * p.rFrac * 0.7;
      const fruit = scene.add.circle(fx, fy, 2.5, fruitColors[i % fruitColors.length]!, 1);
      fruit.setDepth(depth);

      // Pop-in animation
      fruit.setScale(0);
      scene.tweens.add({
        targets: fruit,
        scaleX: 1,
        scaleY: 1,
        duration: 300,
        delay: i * 80,
        ease: "Back.easeOut",
      });

      this.fruits.push(fruit);
    }
  }

  /** Distance (in world tiles) from a point to this tree's tile center. */
  distanceTo(wx: number, wy: number): number {
    const dx = this.worldX + 0.5 - wx;
    const dy = this.worldY + 0.5 - wy;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
